<template>
  <a-input
    v-model:value="displayValue"
    :placeholder="placeholder"
    :disabled="disabled"
    :size="size"
    :allow-clear="allowClear"
    :status="status"
    :addon-after="showSuffix ? currency : undefined"
    class="ant-currency-input"
    @update:value="onInput"
    @blur="onBlur"
    @pressEnter="onPressEnter"
  >
    <template v-if="prefixIcon" #prefix>
      <i :class="prefixIcon" />
    </template>
  </a-input>
</template>

<script lang="ts" setup>
import { ref, watch } from "vue";
import type { SizeType } from "ant-design-vue/es/config-provider";

interface Props {
  modelValue?: number | null;
  placeholder?: string;
  disabled?: boolean;
  size?: SizeType;
  allowClear?: boolean;
  status?: "" | "error" | "warning";
  min?: number;
  max?: number;
  decimal?: number;
  currency?: string;
  showSuffix?: boolean;
  prefixIcon?: string;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  placeholder: "Nhập số tiền",
  disabled: false,
  size: "middle",
  allowClear: true,
  status: "",
  min: 0,
  max: Number.MAX_SAFE_INTEGER,
  decimal: 0,
  currency: "₫",
  showSuffix: true,
  prefixIcon: "",
});

const emit = defineEmits<{
  (e: "update:modelValue", value: number | null): void;
  (e: "blur", event: FocusEvent): void;
  (e: "pressEnter", event: KeyboardEvent): void;
  (e: "change", value: number | null): void;
}>();

const displayValue = ref("");

const clamp = (num: number) => {
  let value = num;
  if (value < props.min) value = props.min;
  if (value > props.max) value = props.max;
  return value;
};

const parseNumber = (value: string): number | null => {
  if (!value || !String(value).trim()) return null;

  let raw = String(value)
    .replace(/[^\d.,]/g, "")
    .replace(/\./g, "")
    .replace(",", ".");

  if (!raw || raw === ".") return null;

  const num = Number(raw);
  if (Number.isNaN(num)) return null;
  return clamp(num);
};

const formatCurrency = (value: number | null): string => {
  if (value === null || value === undefined || Number.isNaN(value)) return "";
  return value.toLocaleString("vi-VN", {
    minimumFractionDigits: props.decimal,
    maximumFractionDigits: props.decimal,
  });
};

watch(
  () => props.modelValue,
  (val) => {
    displayValue.value = formatCurrency(val);
  },
  { immediate: true }
);

const onInput = (val: string) => {
  // Cho phép gõ tự do, chỉ giữ số và dấu phân cách
  displayValue.value = String(val).replace(/[^\d.,]/g, "");
  const parsed = parseNumber(displayValue.value);
  emit("update:modelValue", parsed);
  emit("change", parsed);
};

const onBlur = (e: FocusEvent) => {
  const parsed = parseNumber(displayValue.value);
  displayValue.value = formatCurrency(parsed);
  emit("update:modelValue", parsed);
  emit("blur", e);
};

const onPressEnter = (e: KeyboardEvent) => emit("pressEnter", e);
</script>

<!--
  Hướng dẫn sử dụng — Currency
  Global: <h-currency v-model="price" currency="₫" :decimal="0" />
  Import: import { CurrencyInput } from '@/components/ant/input'

  Props: modelValue (number), min, max, decimal, currency, showSuffix, status...
  Events: update:modelValue, change, blur, pressEnter
  Docs: src/components/ant/input/README.md
-->

<!--
  Hướng dẫn sử dụng — Currency
  Global: <h-currency v-model="price" currency="₫" :decimal="0" />
  Import: import { CurrencyInput } from '@/components/ant/input'

  Props: modelValue (number), min, max, decimal, currency, showSuffix, status...
  Events: update:modelValue, change, blur, pressEnter
  Docs: src/components/ant/input/README.md
-->
